import { Inject, Injectable, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CURRENCY_PIPE } from '@brunsker/libs/core/features/generic-pipe/src';
import {
  FormTableColumns,
  Orcamento,
  TableColumnOptions,
} from '@brunsker/libs/core/ui/table/src';
import { BrazilianCurrencyPipe } from '@brunsker/libs/core/utils/brazilian-currency/src/lib/pipes/brazilian-currency/brazilian-currency.pipe';
import {
  ENVIRONMENT_TOKEN,
  Environment,
} from '@brunsker/libs/core/utils/http-log-interceptor/src';
import { CheckBoxStatus } from '@brunsker/libs/portal-do-vendedor/ui/checkbox-status';
import { TipoVenda } from '@brunsker/libs/portal-do-vendedor/ui/future-order';
import { Product } from '@brunsker/libs/portal-do-vendedor/ui/product-card';
import { ShowEstoqueFiliaisComponent } from '@brunsker/libs/portal-do-vendedor/ui/product-card/lib/components/show-estoque-filiais/show-estoque-filiais.component';
import { LinksSidenav } from '@brunsker/libs/portal-do-vendedor/ui/sidenav';
import {
  NewApiClienteService,
  NewApiOrcamentoService,
  NewApiUserService,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api';
import { PaginatorResponse } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/login.models';
import {
  OrcamentoRequest,
  OrcamentoResponse,
  STATUS_ID_MAP_NUMBER,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/orcamento.models';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  map,
  throttleTime,
} from 'rxjs';
import { AuthApiService } from '../api/auth/auth.service';
import { EnderecoApiService } from '../api/endereco/endereco.service';
import { FiliaisApiService } from '../api/filiais/filiais.service';
import { SelectOption } from '../api/models/generic-form.model';
import {
  OrcamentoDataResponse,
  STATUS_ID_MAP,
  STATUS_MAP,
} from '../api/models/orcamento.model';
import { OrcamentoApiService } from '../api/orcamento/orcamento.service';
import { PlanoPagamentoApiService } from '../api/plano-pagamento/plano-pagamento.service';
import { RegiaoApiService } from '../api/region/region.service';
import { ViaCepService } from '../api/via-cep/via-cep.service';

@Injectable({
  providedIn: 'root',
})
export class MapperService {
  specialFormEvent$$ = new ReplaySubject(1);
  unitaryValue$$ = new BehaviorSubject<number>(1);
  searchType$$ = new ReplaySubject(1);
  hasLoading$ = new BehaviorSubject(false);
  hasOrcamento$$ = new BehaviorSubject({
    show: false,
    orcamentoID: 1,
    seqPedido: 1,
  });
  idVendedor: any;
  planoPagamentoOptions$!: Observable<SelectOption[]>;
  orcamentoTableResponse!: PaginatorResponse<OrcamentoResponse>;

  constructor(
    private newApiUserService: NewApiUserService,
    readonly orcamentoApiService: OrcamentoApiService,
    readonly planoPagamentoApiService: PlanoPagamentoApiService,
    readonly newApiOrcamento: NewApiOrcamentoService,
    readonly regiaoApiService: RegiaoApiService,
    readonly filiaisApiService: FiliaisApiService,
    readonly newApiClienteService: NewApiClienteService,
    readonly enderecoApiService: EnderecoApiService,
    readonly authApiService: AuthApiService,
    readonly viaCep: ViaCepService,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
  ) {}

  createSideNavLinks() {
    const linksNav = [
      {
        title: 'Visão Geral',
        url: '/',
        icon: 'home',
        type: 'link',
      },
      {
        title: 'Produtos',
        url: '/produtos',
        icon: 'grid_view',
        type: 'link',
      },
      {
        title: 'Histórico de Vendas',
        url: '/historico-de-vendas',
        icon: 'history',
        type: 'link',
      },
      {
        title: 'Treinamento',
        url: '/treinamento',
        icon: 'live_help',
        type: 'link',
      },
      0,
    ] as LinksSidenav[];

    if (
      this.newApiUserService.currentUser$$.value?.usuarioMaster === 1 &&
      this.environment.report
    ) {
      const addSupervi = [
        {
          title: 'Relatórios',
          url: '/relatorios',
          icon: 'bar_chart',
          type: 'link',
        },
      ] as LinksSidenav[];

      linksNav.splice(3, 0, ...addSupervi);
    }

    return linksNav;
  }
  generateOptions<T>(
    service: {
      [key: string]: (params?: string | number) => Observable<T[]>;
    } & any,
    method: string,
    property: {
      label: keyof T;
      value: keyof T;
    },
    params?: string | number,
  ) {
    const obs$ = params
      ? (service[method](params) as Observable<T[]>)
      : (service[method]() as Observable<T[]>);
    return obs$.pipe(
      map((data: T[]) => {
        return data.map((item: T) => {
          return {
            label: item[property.label],
            value: item[property.value],
          };
        });
      }),
    );
  }

  createOrcamentoColumnOptions() {
    const orcamentoColumnOptions = [
      {
        columnName: 'Data do orçamento',
      },
      {
        columnName: 'N° Pedido PDV',
      },
      {
        columnName: 'N° Orçamento',
      },
      {
        columnName: 'N° Pedido ERP',
      },
      {
        columnName: 'Cliente',
      },
      {
        columnName: 'Filial',
      },
      {
        columnName: 'Status',
      },
      {
        columnName: 'Vendedor',
      },
      {
        columnName: 'Total',
        fontWeight: 'bold',
      },
    ];
    return orcamentoColumnOptions;
  }

  createPositivacaoTableColumnOptions(template: TemplateRef<unknown>) {
    return [
      {
        columnName: 'Razão Social',
      },
      {
        columnName: 'N° Pedido',
        highlightColor: 'var(--pv-blue-02)',
      },
      {
        columnName: 'Último Pedido',
      },
      {
        columnName: 'Penúltimo Pedido',
      },
      {
        columnName: 'Diferença (em dias)',
      },
      {
        columnName: 'Situação',
        customDom: true,
        template: template,
      },
      {
        columnName: 'Vendedor',
      },
    ];
  }

  createCarteiraTableColumnOptions(template: TemplateRef<unknown>) {
    return [
      {
        columnName: 'Razão Social',
      },
      {
        columnName: 'Código do Cliente',
      },
      {
        columnName: 'N° Pedido',
        highlightColor: 'var(--pv-blue-02)',
      },
      {
        columnName: 'Último Pedido',
      },
      {
        columnName: 'Valor',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Dias em Atraso',
      },
      {
        columnName: 'Situação',
        customDom: true,
        template: template,
      },
      {
        columnName: 'Vendedor',
      },
    ];
  }

  createCurvaAbcTableColumnOptions(template: TemplateRef<unknown>) {
    return [
      {
        columnName: 'Código do Cliente',
      },
      {
        columnName: 'Curva',
        customDom: true,
        template: template,
      },
      {
        columnName: 'Valor em Pedidos',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Porcento',
      },
      {
        columnName: 'Total Vendido',
      },
      {
        columnName: 'Valor de Venda',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
    ];
  }

  createCurvaAbcProdutoTableColumnOptions(template: TemplateRef<unknown>) {
    return [
      {
        columnName: 'Código do Produto',
      },
      {
        columnName: 'Curva',
        customDom: true,
        template: template,
      },
      {
        columnName: 'Total Vendido',
      },
      {
        columnName: 'Valor da Venda',
      },
      {
        columnName: 'Porcento',
      },
      {
        columnName: 'Lucro',
      },
    ];
  }

  createCustomersTableColumnOptions() {
    const tableColumnOptions = [
      {
        columnName: 'Último Pedido',
        // typePipe: DATE_PIPE,
        typePipeParams: ['dd/MM/yyyy'],
      },
      {
        columnName: 'Cód. Cliente',
      },
      {
        columnName: 'Nome',
      },
      {
        columnName: 'CPF/CNPJ',
      },
      {
        columnName: 'Qtd. Pedidos',
      },
      {
        columnName: 'Total Pedido',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
    ];
    return tableColumnOptions;
  }

  createRelatoriosFaturamentoTableColumnOptions() {
    const tableColumnOptions = [
      {
        columnName: 'Emissão',
      },
      {
        columnName: 'Pedido',
      },
      {
        columnName: 'NF',
      },
      {
        columnName: 'Cliente',
      },
      {
        columnName: 'Valor Total',
      },
      {
        columnName: 'Faturado',
      },
      {
        columnName: 'Não Faturado',
      },
      {
        columnName: 'Vendedor',
      },
    ];
    return tableColumnOptions;
  }

  createTipoVendaColumnOptions(subject: ReplaySubject<TipoVenda>) {
    const orcamentoColumnOptions = [
      {
        columnName: 'Data',
      },
      {
        columnName: 'Filial',
      },
      {
        columnName: 'Pedido',
      },
      {
        columnName: 'Orçamento',
      },
      {
        columnName: 'Status',
      },
      {
        columnName: 'Faturado?',
      },

      {
        columnName: 'Ver itens',
        highlightColor: 'var(--pv-primary)',
        cellIsClickable: true,
        clickableCellClickFn: (_: any, orcamento: TipoVenda) => {
          subject.next(orcamento);
        },
      },
    ] as TableColumnOptions[];
    return orcamentoColumnOptions;
  }

  createFiliaisColumnOptions() {
    const filiaisColumnOptions = [
      {
        columnName: 'Filial',
        cellIsClickable: true,
      },
      {
        columnName: 'Endereço',
        cellIsClickable: true,
      },
      {
        columnName: 'Estoque',
        cellIsClickable: true,
      },
      // {
      //   columnName: 'Valor Un.',
      //   cellIsClickable: true,
      //   typePipe: CURRENCY_PIPE,
      //   typePipeParams: ['BRL'],
      // },
    ];
    return filiaisColumnOptions;
  }

  createOrcamentoTableData(status: string) {
    return this.orcamentoApiService
      .filterOrcamento({
        statusped: STATUS_MAP[status],
        index: 0,
        length: 10,
      })
      .pipe(
        map((response: { total: number; data: OrcamentoDataResponse[] }) => {
          const orcamentos = response.data.map(
            (orcamento: OrcamentoDataResponse) => {
              const transformedData = new Date(orcamento.data);

              const date = transformedData.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });

              const isEditable = orcamento.status !== 1;
              const monetaryValue = 'R$ ' + orcamento.vltotal.toFixed(2);

              return {
                data: date,
                orcamentoId: orcamento.seQ_PEDIDO,
                cliente: orcamento.nomE_CLIENTE,
                orcamentoFilial: orcamento.iD_ORCAMENTO_FILIAL,
                status: STATUS_ID_MAP[orcamento.status],
                vendedor: orcamento.nomE_VENDEDOR,
                total: monetaryValue,
                hasView: true,
                hasClose: isEditable,
                hasEdit: isEditable,
              } as Orcamento;
            },
          );
          return orcamentos;
        }),
      );
  }
  createNewApiOrcamentoTableData(request: OrcamentoRequest) {
    return this.newApiOrcamento.getOrcamento(request).pipe(
      throttleTime(500),
      map((response: any) => {
        this.orcamentoTableResponse = response;

        const orcamentos = response.data.map((orcamento: any) => {
          const transformedData = new Date(orcamento.data);
          const date = transformedData.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          const isEditable = orcamento.status !== 1;

          const monetaryValue = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(orcamento.vltotal);

          return {
            data: date,
            seqPedido: orcamento.seQ_PEDIDO,
            orcamentoId: orcamento.iD_ORCAMENTO_FILIAL,
            idPedido: orcamento.iD_PEDIDO === 0 ? '-' : orcamento.iD_PEDIDO,
            cliente: orcamento.nomE_CLIENTE,
            orcamentoFilial: orcamento.iD_FILIAL,
            status: STATUS_ID_MAP_NUMBER[orcamento.status],
            vendedor: orcamento.nomE_VENDEDOR,
            total: monetaryValue,
            totalValue: orcamento.vltotal,
            hasView: true,
            hasClose: isEditable,
            hasEdit: isEditable,
            observacaoPc: orcamento.observacaO_PC,
          } as Orcamento;
        });
        return orcamentos;
      }),
    );
  }

  createPdfColumnOptions() {
    const orcamentoColumnOptions = [
      {
        columnName: 'ITEM',
      },
      {
        columnName: 'CÓDIGO',
      },
      {
        columnName: 'PRODUTO',
      },
      {
        columnName: 'MARCA',
      },
      {
        columnName: 'QT',
      },
      {
        columnName: 'P.UNIT',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'VL.TOTAL',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
    ];
    return orcamentoColumnOptions as TableColumnOptions[];
  }

  createEditarOrcamentoColumnsOptions() {
    const orcamentoColumnOptions = [
      {
        columnName: 'Item',
        columnFormControlName: 'item',
        columnType: 'counter',
      },
      {
        columnName: 'Produto',
        columnFormControlName: 'produto',
        columnType: 'data',
      },
      {
        columnName: 'Embalagem',
        columnFormControlName: 'embalagem',
        columnType: 'data',
      },
      {
        columnName: 'Filial',
        columnFormControlName: 'filial',
        columnType: 'data',
      },
      {
        columnName: 'Estoque',
        columnFormControlName: 'estoque',
        columnType: 'data',
      },
      {
        columnName: 'Desconto/Acréscimo',
        columnFormControlName: 'desconto',
        columnType: 'desconto',
      },
      {
        columnName: 'Valor Unit.',
        columnFormControlName: 'valorUnitario',
        typePipe: BrazilianCurrencyPipe,
        typePipeParams: ['BRL'],
        columnType: 'data',
      },
      {
        columnName: 'Valor unit. Editado',
        columnFormControlName: 'valorUnitarioComDesconto',
        typePipe: BrazilianCurrencyPipe,
        typePipeParams: ['BRL'],
        columnType: 'data',
      },
      // {
      //   columnName: 'Retirada imediata',
      //   columnFormControlName: 'retiradaImediata',
      //   columnType: 'retirada',
      // },
      {
        columnName: 'Quantidade',
        columnFormControlName: 'quantidade',
        columnType: 'incrementor',
      },
      {
        columnFormControlName: 'subTotal',
        columnName: 'Total do item',
        typePipe: BrazilianCurrencyPipe,
        typePipeParams: ['BRL'],
        columnType: 'data',
      },
      {
        columnFormControlName: 'observation',
        columnName: 'Observação',
        columnType: 'observation',
      },
      {
        columnFormControlName: 'remove',
        columnName: 'Remove',
        columnType: 'remove',
      },
    ];
    return orcamentoColumnOptions as FormTableColumns[];
  }

  createTipoVendaColumnsOptions() {
    const orcamentoColumnOptions = [
      {
        columnName: 'Produto',
        columnFormControlName: 'produto',
        columnType: 'data',
      },
      {
        columnName: 'Qtd. original',
        columnFormControlName: 'quantidade',
        columnType: 'data',
      },
      {
        columnName: 'Qtd. devolução Integral',
        columnFormControlName: 'qtdDevolucao',
        columnType: 'data',
      },
      {
        columnName: 'Qtd. entregue TV8',
        columnFormControlName: 'qtdEntregue',
        columnType: 'data',
      },
      {
        columnName: 'Qtd. a entregar',
        columnFormControlName: 'qtdAEntregar',
        columnType: 'data',
      },
      {
        columnName: 'Qtd. solicitada',
        columnFormControlName: 'qtdSolicitada',
        columnType: 'incrementor',
      },
    ];
    return orcamentoColumnOptions as FormTableColumns[];
  }

  createOrcamentoColumnsOptions() {
    const orcamentoColumnOptions = [
      {
        columnName: 'Num',
        columnFormControlName: 'num',
        columnType: 'data',
      },
      {
        columnName: 'Produto',
        columnFormControlName: 'produto',
        columnType: 'data',
      },
      {
        columnName: 'Embalagem',
        columnFormControlName: 'embalagem',
        columnType: 'data',
      },
      {
        columnName: 'Filial',
        columnFormControlName: 'filial',
        columnType: 'data',
      },
      {
        columnName: 'Desconto/Acréscimo',
        columnFormControlName: 'desconto',
        columnType: 'data',
      },
      {
        columnName: 'Valor Unit.',
        columnFormControlName: 'valorUnitario',
        columnType: 'data',
        typePipe: BrazilianCurrencyPipe,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Valor unit. Editado',
        columnFormControlName: 'valorUnitarioComDesconto',
        columnType: 'data',
        typePipe: BrazilianCurrencyPipe,
        typePipeParams: ['BRL'],
      },
      // {
      //   columnName: 'Retirada imediata',
      //   columnFormControlName: 'retiradaImediata',
      //   columnType: 'retirada',
      // },
      {
        columnName: 'Quantidade',
        columnFormControlName: 'quantidade',
        columnType: 'data',
      },
      {
        columnFormControlName: 'subTotal',
        columnName: 'Total do item',
        columnType: 'data',
      },
      {
        columnFormControlName: 'observation',
        columnName: 'observ',
        columnType: 'observation',
      },
    ];
    return orcamentoColumnOptions as FormTableColumns[];
  }

  createCartFormColumnsOptions() {
    const cartColumnOptions = [
      {
        columnName: 'Item',
        columnFormControlName: 'item',
        columnType: 'counter',
      },
      {
        columnName: 'Produto',
        columnFormControlName: 'image',
        columnType: 'image',
      },
      {
        columnName: ' ',
        columnFormControlName: 'produto',
        columnType: 'data',
        columnLabel: 'Nome do produto',
        sortable: true,
        sortKey: 'produto',
      },

      {
        columnName: 'Embalagem',
        columnFormControlName: 'embalagem',
        columnType: 'data',
      },
      {
        columnName: 'Quantidade',
        columnFormControlName: 'quantidade',
        columnType: 'incrementor',
      },
      {
        columnName: 'Desconto/Acréscimo',
        columnFormControlName: 'desconto',
        columnType: 'desconto',
      },
      {
        columnName: 'Valor unit.',
        columnFormControlName: 'valorUnitario',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Valor Unit. Editado',
        columnFormControlName: 'valorUnitarioComDesconto',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      // {
      //   columnName: 'Retirada imediata',
      //   columnFormControlName: 'retiradaImediata',
      //   columnType: 'retirada',
      // },
      {
        columnFormControlName: 'subTotal',
        columnName: 'Total do item',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnFormControlName: 'observation',
        columnName: 'observ',
        columnType: 'observation',
      },
      {
        columnName: 'remove',
        columnFormControlName: 'remove',
        columnType: 'remove',
      },
    ];
    return cartColumnOptions as FormTableColumns[];
  }

  createItemsOrcamentoFormColumnsOptions() {
    const itemsOrcamentoColumnsOptions = [
      {
        columnFormControlName: 'checkboxDesconto',
        columnName: 'Checkbox',
        columnType: 'checkbox',
      },
      {
        columnName: 'Produto ',
        columnFormControlName: 'produto',
        columnType: 'data',
      },
      {
        columnName: 'Embalagem ',
        columnFormControlName: 'embalagem',
        columnType: 'data',
      },
      {
        columnName: 'Estoque ',
        columnFormControlName: 'estoque',
        columnType: 'data',
      },
      {
        columnName: 'Desconto ',
        columnFormControlName: 'desconto',
        columnType: 'desconto',
      },
      {
        columnName: 'Preço gerência ',
        columnFormControlName: 'precoGerencia',
        columnType: 'value-hover',
      },
      {
        columnName: 'Valor Unit.',
        columnFormControlName: 'preco',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Quantidade',
        columnFormControlName: 'quantidade',
        columnType: 'incrementor',
      },
      {
        columnFormControlName: 'subtotal',
        columnName: 'Total do item',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },

      {
        columnFormControlName: 'observation',
        columnName: 'observ',
        columnType: 'observation',
      },
      {
        columnName: 'remove',
        columnFormControlName: 'remove',
        columnType: 'remove',
      },
    ];
    return itemsOrcamentoColumnsOptions as FormTableColumns[];
  }

  createUsersManagementFormColumnsOptions() {
    const userManagementColumnsOptions = [
      {
        columnName: 'RCA',
        columnFormControlName: 'rca',
        columnType: 'data',
      },
      {
        columnName: 'Nome',
        columnFormControlName: 'name',
        columnType: 'data',
      },
      {
        columnName: 'Login',
        columnFormControlName: 'login',
        columnType: 'data',
      },
      {
        columnName: 'Tipo',
        columnFormControlName: 'tipo',
        columnType: 'data',
      },
      {
        columnName: 'Editar',
        columnFormControlName: 'edit',
        columnType: 'edit-client',
      },
      {
        columnName: 'Remover',
        columnFormControlName: 'delete',
        columnType: 'remove',
      },
    ];
    return userManagementColumnsOptions as FormTableColumns[];
  }

  createSearchProductColumnsOptions(matDialog: MatDialog) {
    const editarOrcamentoColumns = [
      {
        columnName: 'Produto',
        columnFormControlName: 'image',
        columnType: 'image',
      },
      {
        columnName: ' ',
        columnFormControlName: 'produto',
        columnType: 'data',
      },
      {
        columnName: 'Tipo de embalagem',
        columnFormControlName: 'tipoEmbalagem',
        columnType: 'packaging-select',
      },
      {
        columnName: 'Filial',
        columnFormControlName: 'filial',
        columnType: 'data',
      },
      {
        columnName: 'Valor Unit.',
        columnFormControlName: 'preco',
        columnType: 'data',
        typePipe: CURRENCY_PIPE,
        typePipeParams: ['BRL'],
      },
      {
        columnName: 'Estoque',
        columnFormControlName: 'estoque',
        columnType: 'data',
        columnIsClickable: {
          cellIsClickable: true,
          highlightColor: 'var(--pv-primary)',
          clickableCellClickFn: (_: any, product: Product) => {
            matDialog.open(ShowEstoqueFiliaisComponent, {
              data: {
                productId: product.id,
              },
            });
          },
        },
      },
      {
        columnName: 'Quantidade',
        columnFormControlName: 'quantidade',
        columnType: 'incrementor',
      },
    ];
    return editarOrcamentoColumns as FormTableColumns[];
  }

  createMetasVendedorColumnsOptions() {
    return ['vendedor', 'metaMes1', 'metaMes2', 'metaMes3', 'metaMes4'];
  }

  checkboxStatus(): CheckBoxStatus[] {
    const checkboxStatus = [
      {
        id: null,
        status: 'Filtrar todos',
        formControl: 'todos',
      },
      {
        id: 0,
        status: 'Orçamento Criado',
        formControl: 'Orçamento-Criado',
      },
      {
        id: 2,
        status: 'Aguardando Integração com o ERP',
        formControl: 'Aguardando-Integração-com-o-Erp',
      },
      {
        id: 1,
        status: 'Integrado com ERP',
        formControl: 'integrado-com-erp',
      },
      {
        id: 3,
        status: 'Pedido Gerado',
        formControl: 'Pedido-Gerado',
      },
      {
        id: 4,
        status: 'Integrado com ERP Pedido',
        formControl: 'Integrado-com-Erp-Pedido',
      },
      {
        id: 5,
        status: 'Aguardando Integração com o ERP Pedido',
        formControl: 'Aguardando-Integração-com-o-Erp-Pedido',
      },
      {
        id: 6,
        status: 'Aguardando Integração do Winthor',
        formControl: 'Aguardando-Integração-do-Winthor',
      },
      {
        id: 7,
        status: 'Pendente',
        formControl: 'Pendente',
      },
      {
        id: 8,
        status: 'Bloqueado',
        formControl: 'Bloqueado',
      },
      {
        id: 9,
        status: 'Liberado',
        formControl: 'Liberado',
      },
      {
        id: 10,
        status: 'Montado',
        formControl: 'Montado',
      },
      {
        id: 11,
        status: 'Faturado',
        formControl: 'Faturado',
      },
      {
        id: 12,
        status: 'Cancelado',
        formControl: 'Cancelado',
      },
      {
        id: 13,
        status: 'Aguardando Liberação',
        formControl: 'Aguardando-Liberação',
      },
      {
        id: 14,
        status: 'Rejeitado',
        formControl: 'Rejeitado',
      },
    ] as CheckBoxStatus[];
    return checkboxStatus;
  }
}
